/* eslint-disable react/require-default-props */
import React from 'react';
import {View, ViewStyle} from 'react-native';
import {noop} from 'lodash';

import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import ArrangedIcons from '/component/partial/ArrangedIcons/ArrangedIcons';
import {Shared} from '/constant/TestIDs';
import {withNavigation} from '/provider/NavigationProvider';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
// ... existing code ...
import {taupe} from '/theme/color/on.color';
import {getTextFromReactNode} from '/util/accessibility.util';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './NavigationHeader.style';
import {getOnIconPressHandler} from './NavigationHeader.util';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}
interface StyleProps {
  base?: ViewStyle;
  header?: ViewStyle;
  arrangedIcons?: ViewStyle;
  iconLeft?: ViewStyle;
  iconsGap?: ViewStyle;
  rightIcons?: ViewStyle;
  title?: ViewStyle;
  paddingTop?: number;
}
interface NavigationHeaderProps {
  style: StyleProps;
  safeAreaInsets: SafeAreaInsets;
  navigateBack: () => void;
  handleShowCreateCandidate: () => void;
  showBack: boolean;
  leftIcons: string[];
  rightIcons: string[];
  onIconPress: (icon: string) => void;
  children: React.ReactNode;
  testID: string;
  title: React.ReactNode;
  accessibilityLabelsLeft: string[];
  accessibilityLabelsRight: string[];
  leftIconTestIDs?: (string | null)[];
  rightIconTestIDs?: (string | null)[];
  titlePressEnabled?: boolean;
  ts: (key: string, fallback: string) => string;
}
export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  style: styleProp = {},
  safeAreaInsets = {top: 0, bottom: 0, left: 0, right: 0},
  navigateBack = noop,
  handleShowCreateCandidate = noop,
  showBack = true,
  leftIcons = [],
  rightIcons = [],
  onIconPress = noop,
  children = null,
  testID = '',
  title = null,
  accessibilityLabelsLeft = [],
  accessibilityLabelsRight = [],
  leftIconTestIDs = [],
  rightIconTestIDs = [],
  titlePressEnabled = true,
  ts = (_key, fallback) => fallback,
}) => {
  const dynamicStyle: ViewStyle = {
    paddingTop: (safeAreaInsets?.top || 0) + (styleProp?.paddingTop || 0),
    ...(styleProp.base || {}),
  };

  const combinedStyle = [styles.base, dynamicStyle];

  const labelsLeft = [...accessibilityLabelsLeft];
  if (showBack) {
    labelsLeft.push(ts('accessibilityBackLabel', 'Go back'));
  }
  const handleOnIconPress = getOnIconPressHandler({
    onIconPress,
    navigateBack,
    handleShowCreateCandidate,
  });

  return (
    <View {...testProperties(testID)} style={combinedStyle}>
      <Gutter
        style={{...styles?.container, ...styleProp.header}}
        accessible={false}>
        <ArrangedIcons
          accessibilityLabelsLeft={labelsLeft}
          accessibilityLabelsRight={accessibilityLabelsRight || []}
          leftIcons={[...(showBack ? ['chevron-left'] : []), ...leftIcons]}
          rightIcons={rightIcons}
          leftIconTestIDs={leftIconTestIDs}
          rightIconTestIDs={rightIconTestIDs}
          onIconPress={handleOnIconPress}
          style={{
            icon: styles.icon,
            iconLeft: {
              ...(styleProp.iconLeft || {}),
              color: taupe,
            },
            base: [styles.icons, styleProp.arrangedIcons],
            iconsGap: {...styleProp.iconsGap},
            iconsRight: {...styleProp.rightIcons},
          }}>
          {title &&
            (titlePressEnabled ? (
              <TouchableOpacity
                style={styles.button}
                onPress={() => handleOnIconPress('chevron-left')}
                accessible={true}
                accessibilityRole="header">
                <Text
                  variant="heading"
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styleProp.title}
                  {...testProperties(Shared.headingTitle)}
                  accessible={true}
                  accessibilityRole="header"
                  accessibilityLabel={`${getTextFromReactNode(title)}`}>
                  {title}
                </Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.button} accessible={false}>
                <Text
                  variant="heading"
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styleProp.title}
                  {...testProperties(Shared.headingTitle)}
                  accessible={true}
                  accessibilityRole="header"
                  accessibilityLabel={`${getTextFromReactNode(title)}`}>
                  {title}
                </Text>
              </View>
            ))}
        </ArrangedIcons>
      </Gutter>
      {children}
    </View>
  );
};

export default withHOC(
  withSafeArea,
  withNavigation,
  withTranslation('layout.navigationHeader'),
)(NavigationHeader);
